import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Shield, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const TwoFactorVerify = ({ email, onBack, onSuccess }) => {
  const { login } = useAuth();
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);

  const handleVerify = async (e) => {
    e.preventDefault();
    
    if (code.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    setVerifying(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/verify-2fa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp_code: code })
      });
      
      if (response.ok) {
        const data = await response.json();
        // Store the token and user data
        localStorage.setItem('formlio_token', data.access_token);
        localStorage.setItem('formlio_user', JSON.stringify(data.user));
        toast.success('Login successful!');
        if (onSuccess) onSuccess(data);
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Invalid OTP code');
      }
    } catch (error) {
      toast.error('Failed to verify OTP');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="text-center">
        <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Shield className="w-8 h-8 text-indigo-600" />
        </div>
        <CardTitle className="text-2xl">Two-Factor Authentication</CardTitle>
        <CardDescription>
          We sent a 6-digit verification code to
          <br />
          <span className="font-medium text-slate-900">{email}</span>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleVerify} className="space-y-4">
          <div className="space-y-2">
            <Input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="Enter 6-digit code"
              className="text-center text-2xl tracking-widest h-14"
              maxLength={6}
              autoFocus
              data-testid="2fa-code-input"
            />
            <p className="text-xs text-slate-500 text-center">
              Code expires in 5 minutes
            </p>
          </div>
          
          <Button 
            type="submit" 
            className="w-full h-12" 
            disabled={verifying || code.length !== 6}
            data-testid="2fa-verify-btn"
          >
            {verifying ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Verifying...
              </>
            ) : (
              'Verify & Sign In'
            )}
          </Button>
        </form>
        
        <div className="mt-6 pt-6 border-t">
          <Button
            variant="ghost"
            className="w-full"
            onClick={onBack}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Login
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default TwoFactorVerify;
